import { URLS } from 'api/constants';
import { ICasinoReview } from 'constants/types';
import { apiGet } from 'lib/api/client';
import { PaginationResponse } from 'lib/api/types';
import { useEffect, useMemo, useState } from 'react';

export const useCasinoReviewsPagination = ({
  casinoId,
}: {
  casinoId: string | string[] | undefined;
}) => {
  const limit = 12;
  const [isFetching, setIsFetching] = useState(false);
  const [count, setCount] = useState(-1);
  const [currentId, setCurrentId] = useState<string | string[] | undefined>(
    casinoId
  );
  const [data, setData] = useState<ICasinoReview[]>([]);

  const [page, setPage] = useState(
    data?.length ? Math.ceil(data?.length / limit) : 1
  );

  useEffect(() => {
    if (currentId !== casinoId) {
      setPage(1);
      setData([]);
      setCount(-1);
      setCurrentId(casinoId);
    }
  }, [currentId, casinoId]);

  useEffect(() => {
    const isCached =
      casinoId === currentId &&
      data?.length &&
      Math.ceil(data?.length / limit) === page;

    if (!isCached && casinoId) {
      setIsFetching(true);
      apiGet<PaginationResponse<ICasinoReview>>(
        `${URLS.CASINO_REVIEWS_BY_CASINO}${casinoId.toString()}`,
        { page, limit }
      )
        .then((response) => {
          const results = response.results ?? [];
          setData((current) =>
            page === 1 ? results : [...current, ...results]
          );
          setCount(Number(response.count ?? results.length));
        })
        .finally(() => setIsFetching(false));
    }
  }, [limit, page, casinoId, currentId, data?.length]);

  const isLast = useMemo(() => {
    if (count === -1) {
      return false;
    }
    return data.length >= count;
  }, [count, data.length]);

  return {
    page,
    data,
    isLast,
    setPage,
    isFetching,
    count: count < 0 ? 0 : count,
  };
};
